import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  POSITION_KIND_CODE_PATTERN,
  positionKindsApi,
  type PositionKindIconName as IconName,
  type PositionKindRequest,
  type PositionKindView,
} from "../../api/structure";
import type { ApiError } from "../../api/client";
import { detailsMap, formatApiError } from "../../lib/apiError";
import { usePositionKinds } from "../../lib/usePositionKinds";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { Modal } from "../../components/Modal";
import Ic from "../../components/icons.jsx";
import { LOCATION_KIND_ICONS, LocationKindIcon } from "../../components/LocationKindIcon";

type FormMode = { kind: "create" } | { kind: "edit"; row: PositionKindView };

/** Predefined dot colours (readable on both themes); anything else goes through "Druga boja". */
const KIND_COLORS: readonly { value: string; label: string }[] = [
  { value: "#0F5BA8", label: "Plava" },
  { value: "#1F8A56", label: "Zelena" },
  { value: "#B6353A", label: "Crvena" },
  { value: "#B97A0E", label: "Oker" },
  { value: "#E0731A", label: "Narandžasta" },
  { value: "#6D28D9", label: "Ljubičasta" },
  { value: "#0E7490", label: "Tirkizna" },
  { value: "#C2185B", label: "Roze" },
  { value: "#5B6B1F", label: "Maslinasta" },
  { value: "#7A4B2A", label: "Braon" },
  { value: "#5A647A", label: "Siva" },
  { value: "#0F1626", label: "Crna" },
];

/**
 * Admin catalogue of position kinds (sprint 5e) — Lokacija, Pogon, Linija,
 * Mašina, Sklop, Deo and whatever the admin adds. The table reads the shared
 * `usePositionKinds` cache; every write refreshes it so the structure page's
 * picker (possibly open in another tab of the SPA) sees the change.
 */
export default function AdminPositionKindsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  // Every codebook has its own functions (V26): position-kinds:create / edit / delete.
  const canCreate = hasPermission("position-kinds:create");
  const canEdit = hasPermission("position-kinds:edit");
  const canDelete = hasPermission("position-kinds:delete");
  const hasActions = canEdit || canDelete;
  const { kinds, loading, error, refresh } = usePositionKinds();
  const [form, setForm] = useState<FormMode | null>(null);

  async function handleDelete(row: PositionKindView) {
    const ok = await confirm({
      title: t("positionKinds.confirmDelete.title", "Brisanje vrste pozicije"),
      message: t("positionKinds.confirmDelete.message", "Obrisati vrstu „{{name}}“ ({{code}})?", { name: row.name, code: row.code }),
      confirmLabel: t("action.delete", "Obriši"),
      danger: true,
    });
    if (!ok) return;
    try {
      await positionKindsApi.remove(row.code);
      toast.success(t("positionKinds.toast.deleted", "Vrsta pozicije obrisana."));
      await refresh();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "IN_USE") {
        const d = detailsMap(e);
        const positions = Number(d?.positionCount ?? row.positionCount);
        const nodes = Number(d?.templateNodeCount ?? 0);
        toast.error(nodes > 0 && positions === 0
          ? t("positionKinds.error.inUseTemplates", "Vrsta se koristi u {{count}} čvorova šablona — prvo izmenite te šablone.", { count: nodes })
          : t("positionKinds.error.inUse", "Vrsta se koristi na {{count}} pozicija — prvo promenite vrstu tim pozicijama.", { count: positions }));
      } else {
        toast.error(formatApiError(e));
      }
    }
  }

  async function handleSaved(created: boolean) {
    setForm(null);
    toast.success(created ? t("positionKinds.toast.created", "Vrsta pozicije dodata.") : t("positionKinds.toast.saved", "Vrsta pozicije sačuvana."));
    await refresh();
  }

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>{t("positionKinds.title", "Vrste pozicija")}</h1>
          <div className="sub">{t("positionKinds.subtitle", "Šifarnik vrsta u drvetu strukture — Lokacija, Pogon, Linija, Mašina, Sklop, Deo… Vrsta određuje ikonicu i grupisanje u sumarnom prikazu.")}</div>
        </div>
        {canCreate && (
          <div className="page-actions">
            <button type="button" className="btn primary" onClick={() => setForm({ kind: "create" })}>
              <Ic.plus /> {t("positionKinds.new", "Nova vrsta")}
            </button>
          </div>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <section className="card">
        {loading ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : kinds.length === 0 ? (
          <div className="empty">{t("positionKinds.empty", "Još nema vrsta pozicija.")}</div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card">
              <thead>
                <tr>
                  <th>{t("positionKinds.field.icon", "Ikonica")}</th>
                  <th>{t("positionKinds.col.code", "Šifra")}</th>
                  <th>{t("positionKinds.col.name", "Naziv")}</th>
                  <th>{t("positionKinds.col.color", "Boja")}</th>
                  <th>{t("positionKinds.col.holdsAsset", "Nosi asset")}</th>
                  <th className="num">{t("positionKinds.col.sortOrder", "Redosled")}</th>
                  <th className="num">{t("positionKinds.col.positionCount", "Pozicija")}</th>
                  {hasActions && <th style={{ textAlign: "right" }}>{t("common.actions", "Radnje")}</th>}
                </tr>
              </thead>
              <tbody>
                {kinds.map((k) => (
                  <tr key={k.code}>
                    <td data-label={t("positionKinds.field.icon", "Ikonica")}>
                      <span title={t(`locationKinds.icon.${k.icon}`, LOCATION_KIND_ICONS[k.icon]?.label ?? "Kutija")}>
                        <LocationKindIcon icon={k.icon} />
                      </span>
                    </td>
                    <td className="mono" data-label={t("positionKinds.col.code", "Šifra")}>{k.code}</td>
                    <td data-label={t("positionKinds.col.name", "Naziv")}><b>{k.name}</b></td>
                    <td data-label={t("positionKinds.col.color", "Boja")}>
                      {k.color ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <span className="kind-dot" style={{ background: k.color, width: 12, height: 12, margin: 0 }} />
                          <span className="mono muted" style={{ fontSize: 12 }}>{k.color}</span>
                        </span>
                      ) : <span className="muted">—</span>}
                    </td>
                    <td data-label={t("positionKinds.col.holdsAsset", "Nosi asset")}>
                      {k.holdsAsset ? t("common.yes", "Da") : <span className="muted">{t("common.no", "Ne")}</span>}
                    </td>
                    <td className="num" data-label={t("positionKinds.col.sortOrder", "Redosled")}>{k.sortOrder}</td>
                    <td className="num" data-label={t("positionKinds.col.positionCount", "Pozicija")}>{k.positionCount}</td>
                    {hasActions && (
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }} data-label={t("common.actions", "Radnje")}>
                        {canEdit && (
                          <button type="button" className="btn sm" onClick={() => setForm({ kind: "edit", row: k })}>
                            {t("action.edit", "Izmeni")}
                          </button>
                        )}
                        {canDelete && (
                          <button type="button" className="btn sm" style={{ ...dangerBtn, marginLeft: 6 }} onClick={() => handleDelete(k)}>
                            {t("action.delete", "Obriši")}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {form && (
        <PositionKindFormModal mode={form} onClose={() => setForm(null)} onSaved={handleSaved} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function PositionKindFormModal({ mode, onClose, onSaved }: {
  mode: FormMode;
  onClose: () => void;
  onSaved: (created: boolean) => Promise<void>;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const editing = mode.kind === "edit" ? mode.row : null;
  const [code, setCode] = useState(editing?.code ?? "");
  const [name, setName] = useState(editing?.name ?? "");
  const [icon, setIcon] = useState<IconName>(editing?.icon ?? "box");
  const [color, setColor] = useState<string | null>(editing?.color ?? null);
  const isCustomColor = color !== null && !KIND_COLORS.some((c) => c.value === color);
  const [holdsAsset, setHoldsAsset] = useState(editing?.holdsAsset ?? true);
  const [sortOrder, setSortOrder] = useState(editing?.sortOrder?.toString() ?? "0");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onCodeChange = useCallback((raw: string) => {
    // Codes are upper-case identifiers; normalise as the admin types.
    setCode(raw.toUpperCase().replace(/[\s-]+/g, "_"));
  }, []);

  useEffect(() => {
    setError(null);
  }, [code, name, sortOrder, icon, color, holdsAsset]);

  const codeValid = editing !== null || POSITION_KIND_CODE_PATTERN.test(code);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!codeValid) {
      setError(t("positionKinds.error.code", "Šifra: veliko slovo pa 1–29 velikih slova, cifara ili donjih crta (npr. HALA, SEKCIJA_2)."));
      return;
    }
    const body: PositionKindRequest = {
      code: editing ? editing.code : code,
      name: name.trim(),
      icon,
      color,
      holdsAsset,
      sortOrder: Number.parseInt(sortOrder, 10) || 0,
    };
    setSubmitting(true);
    try {
      if (editing) await positionKindsApi.update(editing.code, body);
      else await positionKindsApi.create(body);
      await onSaved(editing === null);
    } catch (err) {
      const ae = err as ApiError;
      if (ae.code === "CODE_TAKEN") {
        setError(t("positionKinds.error.codeTaken", "Šifra je već zauzeta."));
      } else if (ae.code === "IN_USE") {
        // Switching "holds asset" off while positions of this kind expect a type or carry an asset.
        const count = Number(detailsMap(ae)?.positionsWithTypeOrAsset ?? 0);
        toast.error(t("positionKinds.error.inUseHoldsAsset", "Ne može: {{count}} pozicija ove vrste ima očekivani tip ili montiran asset.", { count }));
      } else {
        setError(formatApiError(ae));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title={editing ? t("positionKinds.modal.editTitle", "Izmena vrste pozicije") : t("positionKinds.modal.createTitle", "Nova vrsta pozicije")}
      subtitle={editing ? <span style={{ fontFamily: "var(--mono)" }}>{editing.code}</span> : undefined}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {!editing && (
          <label className="field">
            <span>{t("positionKinds.field.code", "Šifra")} <span className="req-mark">*</span></span>
            <input
              className="input"
              value={code}
              onChange={(e) => onCodeChange(e.target.value)}
              required
              maxLength={30}
              autoComplete="off"
              spellCheck={false}
              placeholder="HALA"
              style={{ fontFamily: "var(--mono)", textTransform: "uppercase" }}
              aria-invalid={code.length > 0 && !codeValid}
            />
            <span className="muted" style={{ fontSize: 12 }}>
              {t("positionKinds.field.codeHint", "Velika slova, cifre i donja crta; počinje slovom; 2–30 znakova. Ne menja se posle kreiranja.")}
            </span>
          </label>
        )}
        <div className="form-grid">
          <label className="field">
            <span>{t("positionKinds.field.name", "Naziv")} <span className="req-mark">*</span></span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
          </label>
          <label className="field">
            <span>{t("positionKinds.field.sortOrder", "Redosled")}</span>
            <input className="input" type="number" value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} inputMode="numeric" />
          </label>
        </div>
        <fieldset className="location-icon-field" disabled={submitting} aria-describedby="position-icon-hint">
          <legend>{t("positionKinds.field.icon", "Ikonica")}</legend>
          <div className="location-icon-grid">
            {(Object.keys(LOCATION_KIND_ICONS) as IconName[]).map((value) => (
              <label key={value} className="location-icon-option" title={t(`locationKinds.icon.${value}`, LOCATION_KIND_ICONS[value].label)}>
                <input type="radio" name="position-kind-icon" value={value} checked={icon === value} onChange={() => setIcon(value)} aria-label={t(`locationKinds.icon.${value}`, LOCATION_KIND_ICONS[value].label)} />
                <span className="location-icon-choice">
                  <LocationKindIcon icon={value} />
                </span>
              </label>
            ))}
          </div>
          <p id="position-icon-hint" className="location-icon-hint">
            {t("positionKinds.field.iconHint", "Prikazuje se uz sve pozicije ove vrste u drvetu strukture, kartonu i šablonima.")}
          </p>
        </fieldset>
        <fieldset className="location-icon-field" disabled={submitting} aria-describedby="position-color-hint">
          <legend>{t("positionKinds.field.color", "Boja")}</legend>
          <div className="kind-color-row" role="radiogroup" aria-label={t("positionKinds.field.color", "Boja")}>
            <button
              type="button"
              role="radio"
              aria-checked={color === null}
              className={`kind-color-swatch is-none${color === null ? " active" : ""}`}
              title={t("positionKinds.color.none", "Bez boje")}
              aria-label={t("positionKinds.color.none", "Bez boje")}
              onClick={() => setColor(null)}
            />
            {KIND_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                role="radio"
                aria-checked={color === c.value}
                className={`kind-color-swatch${color === c.value ? " active" : ""}`}
                style={{ background: c.value }}
                title={t(`positionKinds.color.${c.value}`, c.label)}
                aria-label={t(`positionKinds.color.${c.value}`, c.label)}
                onClick={() => setColor(c.value)}
              />
            ))}
            <label
              className={`kind-color-swatch is-custom${isCustomColor ? " active" : ""}`}
              style={isCustomColor && color ? { background: color } : undefined}
              title={t("positionKinds.color.custom", "Druga boja")}
            >
              <input
                type="color"
                value={color ?? "#0F5BA8"}
                onChange={(e) => setColor(e.target.value.toUpperCase())}
                aria-label={t("positionKinds.color.custom", "Druga boja")}
              />
              {!isCustomColor && <span aria-hidden="true">+</span>}
            </label>
          </div>
          <p id="position-color-hint" className="location-icon-hint">
            {color
              ? t("positionKinds.field.colorHint", "Tačkica ove boje stoji uz svaku poziciju ove vrste u drvetu strukture. Izabrano: {{color}}", { color })
              : t("positionKinds.field.colorNoneHint", "Bez boje — pozicije ove vrste nemaju tačkicu u drvetu.")}
          </p>
        </fieldset>
        <label className="check-row">
          <input type="checkbox" checked={holdsAsset} onChange={(e) => setHoldsAsset(e.target.checked)} disabled={submitting} />
          {t("positionKinds.field.holdsAsset", "Nosi asset (mesto za konkretan komad — bez asseta je prazna/crvena)")}
        </label>
        <div className="muted" style={{ fontSize: 12 }}>
          {t("positionKinds.sortHint", "Redosled određuje i podrazumevanu vrstu nove pod-pozicije: sledeća vrsta po redu ispod vrste roditelja.")}
        </div>
        {error && <div style={errorBox} role="alert">{error}</div>}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <button type="submit" className="btn primary" disabled={submitting}>
            {submitting ? t("action.saving", "Čuvanje…") : t("action.save", "Sačuvaj")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
